import React from 'react';
import styled from 'styled-components';
import { Link } from 'gatsby';
import { H1, TextSmall } from 'components/typography';
import ButtonLink from 'components/ButtonLink';
import LogoFooter from 'images/svg/logoFooter.svg';
import SocialMedia from 'components/SocialMedia';
import Copyright from 'components/Copyright';
import './Footer.scss';

const FooterSection = styled.footer`
  background: ${({ theme }) => theme.palette.blackLighten5};
  color: ${({ theme }) => theme.palette.whiteBase};
`;

const InfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8rem;

  ${({ theme }) => theme.breakpoints.maxTablet} {
    flex-direction: column;
    margin-bottom: 4.8rem;
  }
`;

const AddressNavContainer = styled(InfoContainer)`
  margin-bottom: 3.2rem;

  ${({ theme }) => theme.breakpoints.maxTablet} {
    margin-bottom: 2.4rem;
  }
`;

const BrandContainer = styled.div`
  ${({ theme }) => theme.breakpoints.maxTablet} {
    display: flex;
    justify-content: space-between;
    margin-bottom: 4.8rem;
  }
`;

const LogoWrapper = styled(Link)`
  display: block;
  margin-bottom: 4.8rem;

  ${({ theme }) => theme.breakpoints.maxTablet} {
    margin-bottom: 0;

    svg {
      height: 1.6rem;
      width: 8.6rem;
    }
  }
`;

const LogoSubTitle = styled(TextSmall)`
  line-height: 1.6rem;
  color: ${({ theme }) => theme.palette.navyGrayDarken5};

  ${({ theme }) => theme.breakpoints.maxTablet} {
    font-size: 1rem;
    line-height: 2;
  }
`;

const Address = styled.p`
  font-size: 2rem;
  line-height: 1.6;

  ${({ theme }) => theme.breakpoints.maxTablet} {
    font-size: 1.6rem;
    line-height: 1.5;
    margin-bottom: 4.8rem;
  }
`;

const Invitation = styled(H1)`
  color: inherit;
  margin-bottom: 3.2rem;

  ${({ theme }) => theme.breakpoints.maxTablet} {
    margin-bottom: 2.4rem;
  }
`;

const NavContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-gap: 4.8rem;
  align-items: center;

  ${({ theme }) => theme.breakpoints.maxTablet} {
    grid-template: repeat(3, 1fr) / repeat(2, 1fr);
    grid-gap: 2.4rem;
    grid-auto-flow: column;
    width: 100%;

    a {
      width: max-content;
    }
  }
`;

const Line = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.palette.navyGrayDarken15};
  margin-bottom: 3.2rem;

  ${({ theme }) => theme.breakpoints.maxTablet} {
    margin-bottom: 2.4rem;
  }
`;

const Flex = styled.div`
  display: flex;
  justify-content: space-between;

  ${({ theme }) => theme.breakpoints.maxTablet} {
    flex-direction: column-reverse;

    a {
      width: max-content;
      margin-bottom: 2.4rem;
    }
  }
`;

const data = {
  logoSubTitle: 'We design to impact the brand',
  contact: {
    invitation: `Let's talk`,
    email: {
      name: 'hello@markedway.co.uk',
    },
    address: '34 Winston Close, Romford, RM7 8LL',
  },
};

const Footer = ({ navigation }) => (
  <FooterSection className="footer">
    <div className="footer__footer-container">
      <InfoContainer>
        <BrandContainer>
          <LogoWrapper to="/">
            <LogoFooter />
          </LogoWrapper>
          <LogoSubTitle>{data.logoSubTitle}</LogoSubTitle>
        </BrandContainer>

        <div>
          <Invitation>{data.contact.invitation}</Invitation>
          <ButtonLink
            as="a"
            variant="secondary"
            size="lg"
            href={`mailto:${data.contact.email.name}`}
            link={data.contact.email}
          />
        </div>
      </InfoContainer>
      <AddressNavContainer>
        <Address>{data.contact.address}</Address>
        <NavContainer>
          {navigation.map(({ link }) => (
            <ButtonLink key={link.to} link={link} variant="secondary" size="lg" />
          ))}
        </NavContainer>
      </AddressNavContainer>
      <Line />

      <Flex>
        <Copyright />
        <ButtonLink
          link={{ name: 'Privacy Policy', to: '/privacy-policy', href: '/privacy-policy' }}
          variant="secondary"
          size="sm"
        />
        <SocialMedia />
      </Flex>
    </div>
  </FooterSection>
);

export default Footer;
